import type { MouseEvent, ReactNode } from 'react';
import React, { useContext, useState } from 'react';

import { token } from '@atlaskit/tokens';
import Popup from '@atlaskit/popup';
import { WidthObserver } from '@atlaskit/width-detector';

import { useSessionData } from '@confluence/session-data';

import type { SpaceProp, ResponsiveCategoriesProps } from '../types';
import { FiltersContext } from '../Filters';

import { Category, CategoriesContainer } from './PresentationalComponents';
import { useOverflow } from './useOverflow';

type Label = {
	id: string;
	prefix: string;
	name: string;
};

const CategoriesOverflow = ({ overflowed }) => {
	const [isOpen, setIsOpen] = useState(false);

	const onTriggerClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsOpen(!isOpen);
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			trigger={(props) => (
				<Category {...props} onClick={onTriggerClick}>
					+{overflowed.length}
				</Category>
			)}
			content={() => (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				<div style={{ padding: token('space.150', '12px') }}>{overflowed}</div>
			)}
			placement="bottom"
		/>
	);
};

export const Categories = ({
	space,
	categoriesWidth,
	setCategoriesWidth,
}: SpaceProp & ResponsiveCategoriesProps) => {
	const { isLicensed } = useSessionData();
	const labels = space.metadata?.labels?.nodes || [];
	const { updateLabel } = useContext(FiltersContext);

	const onClick = (label: string) => (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (isLicensed) {
			updateLabel(label);
		}
	};

	const items: Record<string, ReactNode> = {};
	labels
		.filter(
			(label): label is Label =>
				label != null && label.id != null && label.name != null && label.prefix !== 'my',
		)
		.forEach((label) => {
			items[label.id] = <Category onClick={onClick(label.name)}>{label.name}</Category>;
		});

	const { visible, overflowed } = useOverflow(categoriesWidth - 40, items);

	const shouldRender = categoriesWidth >= 120;

	return (
		<CategoriesContainer>
			{setCategoriesWidth && <WidthObserver setWidth={setCategoriesWidth} offscreen />}
			{shouldRender && visible}
			{shouldRender && overflowed.length > 0 && <CategoriesOverflow overflowed={overflowed} />}
		</CategoriesContainer>
	);
};
